import { useDispatch, useSelector } from 'react-redux'
import {
  QueryParams,
  aggregates,
  chartTypes,
  disaggregations,
  endpointLabels,
  mapTypes,
} from '../common/utils/enum'
import {
  setAggregateData,
  setFilteredAggregateData,
  setIsAggregateDataLoading,
  resetSelectedCountries,
  setSelectedAverage,
  setSelectedAverages,
  setIsLoading,
} from './indicatorProfileSlice'
import { useEffect, useRef, useState } from 'react'
import {
  buildFilterAttributeParams,
  clone,
  formatFiltersForDataAPI,
  getAPIHeaders,
  getEndpointFromConfig,
  getMappingForBenchmark,
  getNameFromDisaggregation,
} from '../common/utils/helper'
import { fetchAndSetData } from '../common/commonSlice'
import {
  ALL_COUNTRIES_OBJECT,
  GROUP_ORDER,
  INCOME_GROUPS_ORDER,
  NONE,
  TOTAL,
  WORLD_DEFAULT,
} from '../common/utils/constants'
import useQueryParams from '../common/hooks/useQueryParams'
import useCloseOnOutsideClick from '../common/hooks/useCloseOnOutsideClick'

const AverageDropdown = ({
  chartType,
  callDimensionsAPIImmediate,
  validateAndUpdateFilters,
  validateAndUpdateUnitMeasureAndFrequency,
}) => {
  const { endpointList, featureProps, grouping, apiKeyName, apiKeyValue } =
    useSelector((state) => state.common)
  const { site } = featureProps
  const {
    disaggregationData,
    disaggregationCountries,
    selectedAverage,
    selectedAverages,
    indicatorCode,
    indicatorMetadata,
    selectedCountries,
    selectedCountriesForBar,
    aggregateData,
    selectedFilters,
    selectedUnitMeasure,
    selectedFrequency,
    indicatorMaxYear,
    selectedComparison,
    recentYearSelection,
    selectedSingleYear,
    selectedMinYear,
    selectedMaxYear,
    countryHierarchy,
    isSelectedFiltersUpdated,
    dimensionsData,
    changedFilterName,
    boundaryType,
    isDimensionsLoading,
    refGroupings,
  } = useSelector((state) => state.indicatorProfile)

  const [options, setOptions] = useState([])
  const dispatch = useDispatch()
  const { setQueryParam, getQueryParam, deleteQueryParams } = useQueryParams()

  const [minYearFromParams, setMinYearFromParams] = useState(null)
  const [maxYearFromParams, setMaxYearFromParams] = useState(null)
  const [singleYearFromParams, setSingleYearFromParams] = useState(null)
  const [averageFromParams, setAverageFromParams] = useState(null)
  const [showAverageDropdown, setShowAverageDropdown] = useState(false)
  const [multiselectOptions, setMultiselectOptions] = useState([])
  const [availableAverageOptions, setAvailableAverageOptions] = useState(null)
  const [pendingAverageSelection, setPendingAverageSelection] = useState(null)

  const averageDropdownRef = useRef(null)

  useCloseOnOutsideClick(averageDropdownRef, () =>
    setShowAverageDropdown(false)
  )

  const isSingleSelectionGrouping =
    indicatorMetadata?.series_description?.database_id === 'IFC_GB' ||
    chartType === chartTypes.StackedBar ||
    chartType === chartTypes.Pie

  useEffect(() => {
    if (getQueryParam(QueryParams.MinYear)) {
      setMinYearFromParams(getQueryParam(QueryParams.MinYear))
    }
    if (getQueryParam(QueryParams.MaxYear)) {
      setMaxYearFromParams(getQueryParam(QueryParams.MaxYear))
    }
    if (getQueryParam(QueryParams.Year)) {
      setSingleYearFromParams(getQueryParam(QueryParams.Year))
    }
    if (getQueryParam(QueryParams.Average)) {
      isSingleSelectionGrouping
        ? setAverageFromParams(getQueryParam(QueryParams.Average))
        : setAverageFromParams(getQueryParam(QueryParams.Average).split(','))
      if (!isSingleSelectionGrouping)
        dispatch(
          setSelectedAverages(getQueryParam(QueryParams.Average).split(','))
        )
    }
  }, [])

  useEffect(() => {
    if (
      dimensionsData &&
      dimensionsData &&
      changedFilterName !== disaggregations.RefArea
    ) {
      const availableRefAreas = dimensionsData[disaggregations.RefArea]
      if (
        availableRefAreas &&
        Array.isArray(availableRefAreas) &&
        availableRefAreas.length > 0
      ) {
        setAvailableAverageOptions(availableRefAreas)

        if (isSingleSelectionGrouping) {
          const currentAverage = selectedAverage[chartType]
          if (
            currentAverage &&
            currentAverage !== NONE &&
            !availableRefAreas.includes(currentAverage)
          ) {
            const firstAvailable = availableRefAreas[0] || NONE
            dispatch(
              setSelectedAverage({
                chartType,
                average: firstAvailable,
              })
            )
            setQueryParam(QueryParams.Average, firstAvailable)
          }
        } else {
          if (selectedAverages && selectedAverages.length > 0) {
            const validAverages = selectedAverages.filter((avg) => {
              if (avg === aggregates.Region.id) {
                const regionGroups = countryHierarchy?.REGION || []
                const availableRegions = regionGroups.filter((region) =>
                  availableRefAreas.includes(region.id)
                )
                return availableRegions.length > 0
              }

              if (avg === aggregates.IncomeGroup.id) {
                const incomeGroups = countryHierarchy?.INCOME || []
                const availableIncomeGroups = incomeGroups.filter((income) =>
                  availableRefAreas.includes(income.id)
                )
                return availableIncomeGroups.length > 0
              }

              return availableRefAreas.includes(avg)
            })

            if (validAverages.length !== selectedAverages.length) {
              if (validAverages.length > 0) {
                dispatch(setSelectedAverages(validAverages))
                setQueryParam(QueryParams.Average, validAverages.join(','))
              } else {
                dispatch(setSelectedAverages([]))
                deleteQueryParams([QueryParams.Average])
              }
            }
          }
        }
      } else {
        setAvailableAverageOptions(null)
      }
    } else {
      setAvailableAverageOptions(null)
    }
  }, [
    dimensionsData,
    changedFilterName,
    selectedAverage,
    selectedAverages,
    chartType,
    isSingleSelectionGrouping,
  ])

  const isChecked = (value) => {
    const activeSelection =
      pendingAverageSelection !== null
        ? isDimensionsLoading
          ? pendingAverageSelection
          : (selectedAverages ?? pendingAverageSelection)
        : selectedAverages
    return activeSelection.includes(value)
  }

  const isOptionDisabled = (value) => {
    if (!availableAverageOptions) {
      return false
    }

    if (value === aggregates.Region.id) {
      const regionGroups = countryHierarchy?.REGION || []
      const availableRegions = regionGroups.filter((region) =>
        availableAverageOptions.includes(region.id)
      )
      return availableRegions.length === 0
    }

    if (value === aggregates.IncomeGroup.id) {
      const incomeGroups = countryHierarchy?.INCOME || []
      const availableIncomeGroups = incomeGroups.filter((income) =>
        availableAverageOptions.includes(income.id)
      )
      return availableIncomeGroups.length === 0
    }

    return !availableAverageOptions.includes(value)
  }

  const hasDisabledOptions = multiselectOptions?.some((item) =>
    isOptionDisabled(item.id)
  )

  const buildGroupedOptionsFromHierarchy = () => {
    if (!countryHierarchy || !disaggregationCountries.length) {
      return []
    }

    const groupedOptions = []
    const availableCountryIds = disaggregationCountries.map((item) => item.code)
    const regionCodes = grouping?.['HCL_WB_REGIONS']
      ? grouping['HCL_WB_REGIONS'].map((region) => region.id)
      : []

    Object.keys(countryHierarchy).forEach((groupType) => {
      if (
        chartType === chartTypes.Map &&
        groupType !==
          (boundaryType === mapTypes.Region
            ? 'REGION'
            : boundaryType === mapTypes.Continent
              ? 'CONTINENT'
              : 'INCOME')
      ) {
        return
      }

      const groups = countryHierarchy[groupType]
      if (Array.isArray(groups)) {
        groups.forEach((group) => {
          const hasAvailableCountries = availableCountryIds.includes(group.id)

          const isPrimaryRegion =
            chartType === chartTypes.Map && groupType === 'REGION'
              ? regionCodes.includes(group.id)
              : true

          if (
            hasAvailableCountries &&
            isPrimaryRegion &&
            groupType !== WORLD_DEFAULT?.id &&
            groupType !== TOTAL
          ) {
            groupedOptions.push({
              id: group.id,
              // TO-DO - need to check on the proper implementation
              value:
                getNameFromDisaggregation(
                  disaggregationData?.dimensions,
                  disaggregations.RefArea,
                  group.id
                ) || group.name,
              groupType: groupType,
            })
          }
        })
      }
    })

    return groupedOptions
  }

  useEffect(() => {
    let options = [
      <option
        key={`${chartType}_none_aggregate`}
        value={NONE}
        className="ng-star-inserted"
      >
        {NONE}
      </option>,
    ]
    let averageOptions = []

    if (disaggregationCountries.length > 0) {
      if (!isSingleSelectionGrouping) {
        if (countryHierarchy) {
          const hierarchyOptions = buildGroupedOptionsFromHierarchy()

          if (
            chartType !== chartTypes.Map &&
            disaggregationCountries
              .map((item) => item.code)
              .includes(WORLD_DEFAULT?.id)
          ) {
            averageOptions.push({
              id: WORLD_DEFAULT?.id,
              value:
                getNameFromDisaggregation(
                  disaggregationData?.dimensions,
                  disaggregations.RefArea,
                  WORLD_DEFAULT?.id
                ) || WORLD_DEFAULT?.name,
              groupType: WORLD_DEFAULT?.name?.toUpperCase(),
            })
            if (
              chartType === chartTypes.Trend &&
              !averageFromParams &&
              selectedAverages.length === 0 &&
              selectedCountries?.length === 0
            ) {
              dispatch(setSelectedAverages([WORLD_DEFAULT?.id]))
              setQueryParam(QueryParams.Average, WORLD_DEFAULT?.id)
            }
          }

          if (
            chartType !== chartTypes.Map &&
            disaggregationCountries.map((item) => item.code).includes(TOTAL)
          ) {
            averageOptions?.push({
              id: TOTAL,
              value:
                getNameFromDisaggregation(
                  disaggregationData?.dimensions,
                  disaggregations.RefArea,
                  TOTAL
                ) || 'Overall',
              groupType: WORLD_DEFAULT?.name?.toUpperCase(),
            })

            // Set Overall as default for Trend chart if World is not available
            if (
              chartType === chartTypes.Trend &&
              !averageFromParams &&
              selectedAverages.length === 0 &&
              selectedCountries?.length === 0 &&
              !disaggregationCountries
                .map((item) => item.code)
                .includes(WORLD_DEFAULT?.id)
            ) {
              dispatch(setSelectedAverages([TOTAL]))
              setQueryParam(QueryParams.Average, TOTAL)
            }
          }

          const regionGroups = hierarchyOptions.filter(
            (o) => o.groupType === 'REGION'
          )
          const incomeGroups = hierarchyOptions.filter(
            (o) => o.groupType === 'INCOME'
          )

          let aggregateOpts = []
          if (regionGroups.length > 0 && chartType !== chartTypes.Map) {
            aggregateOpts.push({
              id: aggregates.Region.id,
              value: aggregates.Region.name,
              groupType: 'AGGREGATE',
            })
          }
          if (incomeGroups.length > 0 && chartType !== chartTypes.Map) {
            aggregateOpts.push({
              id: aggregates.IncomeGroup.id,
              value: aggregates.IncomeGroup.name,
              groupType: 'AGGREGATE',
            })
          }

          averageOptions.push(...aggregateOpts, ...hierarchyOptions)
        }
      } else {
        if (indicatorMetadata?.series_description?.database_id !== 'IFC_GB') {
          const hierarchyOptions = buildGroupedOptionsFromHierarchy()

          if (
            disaggregationCountries
              .map((item) => item.code)
              .includes(WORLD_DEFAULT?.id)
          ) {
            options.push(
              <option
                key={`${chartType}_world_${WORLD_DEFAULT?.id}`}
                value={WORLD_DEFAULT?.id}
                className="ng-star-inserted"
                disabled={isOptionDisabled(WORLD_DEFAULT?.id)}
              >
                {getNameFromDisaggregation(
                  disaggregationData?.dimensions,
                  disaggregations.RefArea,
                  WORLD_DEFAULT?.id
                ) || WORLD_DEFAULT?.name}
              </option>
            )
          }

          if (
            disaggregationCountries.map((item) => item.code).includes(TOTAL)
          ) {
            options.push(
              <option
                key={`${chartType}_total_${TOTAL}`}
                value={TOTAL}
                className="ng-star-inserted"
                disabled={isOptionDisabled(TOTAL)}
              >
                {getNameFromDisaggregation(
                  disaggregationData?.dimensions,
                  disaggregations.RefArea,
                  TOTAL
                ) || 'Overall'}
              </option>
            )
          }

          hierarchyOptions.forEach((item) => {
            options.push(
              <option
                key={`${chartType}_hierarchy_${item.id}`}
                value={item.id}
                className="ng-star-inserted"
                disabled={isOptionDisabled(item.id)}
              >
                {item.value}
              </option>
            )
          })
        } else {
          const availableIfcGrpList =
            refGroupings?.filter((id) =>
              disaggregationCountries.some((c) => c.code === id)
            ) || []
          const tempIfcGroupList = availableIfcGrpList.map((id) => ({
            id,
            name: getNameFromDisaggregation(
              disaggregationData?.dimensions,
              disaggregations.RefArea,
              id
            ),
          }))

          const sortedIfcGroupList = tempIfcGroupList.sort((a, b) =>
            a.name.localeCompare(b.name)
          )

          sortedIfcGroupList.forEach((item) => {
            options.push(
              <option
                key={`${chartType}_benchmark_${item.id}`}
                value={item.id}
                className="ng-star-inserted"
                disabled={isOptionDisabled(item.id)}
              >
                {item.name}
              </option>
            )
          })
        }
      }
      isSingleSelectionGrouping
        ? setOptions(options)
        : setMultiselectOptions(averageOptions)

      if (averageFromParams) {
        isSingleSelectionGrouping
          ? dispatch(
              setSelectedAverage({ chartType, average: averageFromParams })
            )
          : dispatch(setSelectedAverages(averageFromParams))
        setAverageFromParams(null)
      }
    }
  }, [
    disaggregationCountries,
    chartType,
    countryHierarchy,
    indicatorMetadata,
    availableAverageOptions,
    boundaryType,
    refGroupings,
  ])

  useEffect(() => {
    const fetchAggregateData = async () => {
      dispatch(setIsAggregateDataLoading(true))

      let response = []
      try {
        if (
          (isSingleSelectionGrouping &&
            selectedAverage[chartType] &&
            selectedAverage[chartType] !== NONE) ||
          (!isSingleSelectionGrouping &&
            selectedAverages &&
            selectedAverages.length > 0)
        ) {
          let params = `database_id=${
            indicatorMetadata?.series_description?.database_id
          }&indicator_id=${indicatorCode.trim().toUpperCase()}`
          let filters = []
          if (Object.keys(selectedFilters)?.length > 0) {
            for (const [keyName, value] of Object.entries(selectedFilters)) {
              if (keyName && value) {
                filters.push({
                  filterColumn: keyName,
                  filterValue: value,
                })
              }
            }
          } else {
            filters = buildFilterAttributeParams(
              disaggregationData?.dimensions,
              filters
            )
          }
          if (isSingleSelectionGrouping) {
            filters.push({
              filterColumn: disaggregations.RefArea,
              filterValue: selectedAverage[chartType],
            })
          } else {
            let benchmarkList = []
            let averages = clone(selectedAverages)
            averages = averages.filter(
              (item) =>
                item !== aggregates.Region.id &&
                item !== aggregates.IncomeGroup.id
            )

            if (
              chartType === chartTypes.Map &&
              boundaryType === mapTypes.Region
            ) {
              const regionCodes = grouping?.['HCL_WB_REGIONS']
                ? grouping['HCL_WB_REGIONS'].map((region) => region.id)
                : []
              averages = averages.filter((item) => regionCodes.includes(item))
            }

            if (selectedAverages.includes(aggregates.Region.id)) {
              const regionCountriesMapping = countryHierarchy?.REGION || []

              const regionList =
                chartType === chartTypes.Bar
                  ? selectedCountriesForBar.length > 0 &&
                    !selectedCountriesForBar.includes(
                      ALL_COUNTRIES_OBJECT.name.toLowerCase()
                    )
                    ? getMappingForBenchmark(
                        selectedCountriesForBar,
                        regionCountriesMapping
                      )
                    : regionCountriesMapping?.filter((region) =>
                        disaggregationCountries?.find(
                          (country) => country.code === region.id
                        )
                      )
                  : selectedCountries.length > 0
                    ? getMappingForBenchmark(
                        selectedCountries,
                        regionCountriesMapping
                      )
                    : regionCountriesMapping?.filter((region) =>
                        disaggregationCountries?.find(
                          (country) => country.code === region.id
                        )
                      )
              benchmarkList.push(...regionList.map((item) => item.id))
            }
            if (selectedAverages.includes(aggregates.IncomeGroup.id)) {
              const incomeGrpCountriesMapping = countryHierarchy?.INCOME || []

              const incomeGroupList =
                chartType === chartTypes.Bar
                  ? selectedCountriesForBar.length > 0 &&
                    !selectedCountriesForBar.includes(
                      ALL_COUNTRIES_OBJECT.name.toLowerCase()
                    )
                    ? getMappingForBenchmark(
                        selectedCountriesForBar,
                        incomeGrpCountriesMapping
                      )
                    : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                        disaggregationCountries?.find(
                          (country) => country.code === incomeGroup.id
                        )
                      )
                  : selectedCountries.length > 0
                    ? getMappingForBenchmark(
                        selectedCountries,
                        incomeGrpCountriesMapping
                      )
                    : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                        disaggregationCountries?.find(
                          (country) => country.code === incomeGroup.id
                        )
                      )
              benchmarkList.push(...incomeGroupList.map((item) => item.id))
            }
            //avoid duplicates in regional/income group aggregates and individual regions/income groups
            const refAreas = new Set([...benchmarkList, ...averages])
            filters.push({
              filterColumn: disaggregations.RefArea,
              filterValue: Array.from(refAreas).join(','),
            })
          }
          if (
            chartType !== chartTypes.Trend &&
            chartType !== chartTypes.StackedBar
          ) {
            if (
              indicatorMetadata?.admin_metadata?.[site]?.visualization
                ?.temporal_default === 'latest_data' &&
              recentYearSelection
            ) {
              params += `&timePeriodFrom=${indicatorMaxYear}&timePeriodTo=${indicatorMaxYear}`
            } else if (recentYearSelection) {
              params += '&isLatestData=true'
            } else {
              params +=
                chartType === chartTypes.Bar ||
                chartType === chartTypes.DataTable
                  ? `&timePeriodFrom=${selectedMinYear}&timePeriodTo=${selectedMaxYear}`
                  : `&timePeriodFrom=${selectedSingleYear}&timePeriodTo=${selectedSingleYear}`
            }
          }
          const endpoint = `${
            getEndpointFromConfig(endpointList, endpointLabels.Data).apiEndpoint
          }?${params}${
            filters?.length
              ? `&filters=${formatFiltersForDataAPI(filters)}`
              : ''
          }`
          response = await dispatch(
            fetchAndSetData({
              endpoint,
              key: endpoint,
              headers: getAPIHeaders({
                apiKeyName,
                apiKeyValue,
              }),
            })
          )
        }
        dispatch(setAggregateData(response.payload || []))
      } catch (error) {
        console.error('Error fetching  data:', error)
      } finally {
        dispatch(setIsLoading(false))
        dispatch(setIsAggregateDataLoading(false))
      }
    }
    if (indicatorMetadata) {
      if (
        ((!isSingleSelectionGrouping &&
          selectedAverages &&
          selectedAverages.length > 0) ||
          (isSingleSelectionGrouping &&
            selectedAverage[chartType] &&
            selectedAverage[chartType] !== NONE)) &&
        indicatorMetadata &&
        indicatorCode &&
        isSelectedFiltersUpdated
      ) {
        fetchAggregateData()
      } else if (
        (!isSingleSelectionGrouping &&
          selectedAverages &&
          selectedAverages.length === 0) ||
        (isSingleSelectionGrouping && selectedAverage[chartType] === NONE)
      ) {
        dispatch(setAggregateData([]))
      }
    }
  }, [
    selectedAverage[chartType],
    selectedAverages,
    selectedCountries,
    selectedCountriesForBar,
    indicatorMetadata,
    indicatorCode,
    disaggregationData,
    selectedFilters,
    recentYearSelection,
    selectedSingleYear,
    chartType,
    isSelectedFiltersUpdated,
  ])

  useEffect(() => {
    const filterAggregateData = () => {
      if (
        chartType === chartTypes.Trend &&
        chartType === chartTypes.StackedBar &&
        selectedComparison !== NONE &&
        selectedFilters[selectedComparison] === ''
      ) {
        dispatch(setFilteredAggregateData([]))
      } else {
        const filteredData = aggregateData.filter((item) => {
          const checkFilter = (filter, value) => {
            if (!filter) return true
            const filterValues = filter.split(',')
            return filterValues.includes(value)
          }

          const passesSelectedFilters = Object.entries(selectedFilters)?.every(
            ([filterKey, filterValue]) =>
              checkFilter(filterValue, item[filterKey])
          )

          const passesFrequency =
            !selectedFrequency ||
            selectedFrequency
              .split(',')
              .includes(item?.[disaggregations.Frequency])

          const passesUnitMeasure =
            !selectedUnitMeasure ||
            selectedUnitMeasure
              .split(',')
              .includes(item?.[disaggregations.UnitMeasure])

          return passesSelectedFilters && passesFrequency && passesUnitMeasure
        })
        dispatch(setFilteredAggregateData(filteredData))
      }
    }

    filterAggregateData()
  }, [aggregateData, selectedFilters, selectedUnitMeasure, selectedFrequency])

  const handleSelectAllChange = async (event) => {
    const allValues = event.target.checked
      ? multiselectOptions.map((item) => item.id)
      : []

    setPendingAverageSelection(allValues)

    try {
      if (allValues.length > 0) {
        let benchmarkList = []
        let averages = clone(allValues)
        averages = averages.filter(
          (item) =>
            item !== aggregates.Region.id && item !== aggregates.IncomeGroup.id
        )

        if (chartType === chartTypes.Map && boundaryType === mapTypes.Region) {
          const regionCodes = grouping?.['HCL_WB_REGIONS']
            ? grouping['HCL_WB_REGIONS'].map((region) => region.id)
            : []
          averages = averages.filter((item) => regionCodes.includes(item))
        }

        if (allValues.includes(aggregates.Region.id)) {
          const regionCountriesMapping = countryHierarchy?.REGION || []
          const regionList =
            chartType === chartTypes.Bar
              ? selectedCountriesForBar.length > 0 &&
                !selectedCountriesForBar.includes(
                  ALL_COUNTRIES_OBJECT.name.toLowerCase()
                )
                ? getMappingForBenchmark(
                    selectedCountriesForBar,
                    regionCountriesMapping
                  )
                : regionCountriesMapping?.filter((region) =>
                    disaggregationCountries?.find(
                      (country) => country.code === region.id
                    )
                  )
              : selectedCountries.length > 0
                ? getMappingForBenchmark(
                    selectedCountries,
                    regionCountriesMapping
                  )
                : regionCountriesMapping?.filter((region) =>
                    disaggregationCountries?.find(
                      (country) => country.code === region.id
                    )
                  )
          benchmarkList.push(...regionList.map((item) => item.id))
        }

        if (allValues.includes(aggregates.IncomeGroup.id)) {
          const incomeGrpCountriesMapping = countryHierarchy?.INCOME || []
          const incomeGroupList =
            chartType === chartTypes.Bar
              ? selectedCountriesForBar.length > 0 &&
                !selectedCountriesForBar.includes(
                  ALL_COUNTRIES_OBJECT.name.toLowerCase()
                )
                ? getMappingForBenchmark(
                    selectedCountriesForBar,
                    incomeGrpCountriesMapping
                  )
                : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                    disaggregationCountries?.find(
                      (country) => country.code === incomeGroup.id
                    )
                  )
              : selectedCountries.length > 0
                ? getMappingForBenchmark(
                    selectedCountries,
                    incomeGrpCountriesMapping
                  )
                : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                    disaggregationCountries?.find(
                      (country) => country.code === incomeGroup.id
                    )
                  )
          benchmarkList.push(...incomeGroupList.map((item) => item.id))
        }

        const refAreas = new Set([...benchmarkList, ...averages])
        const countriesToInclude =
          chartType === chartTypes.Bar
            ? selectedCountriesForBar.filter(
                (item) => item !== ALL_COUNTRIES_OBJECT.name.toLowerCase()
              )
            : selectedCountries
        countriesToInclude.forEach((country) => refAreas.add(country))
        const allRefAreas = Array.from(refAreas)

        const dimensions = await callDimensionsAPIImmediate(
          disaggregations.RefArea,
          allRefAreas.join(',')
        )

        if (dimensions) {
          validateAndUpdateFilters(dimensions)
          validateAndUpdateUnitMeasureAndFrequency(dimensions)
        }
      } else {
        const countriesToInclude =
          chartType === chartTypes.Bar
            ? (selectedCountriesForBar || []).filter(
                (item) => item !== ALL_COUNTRIES_OBJECT.name.toLowerCase()
              )
            : selectedCountries || []
        const refAreas =
          countriesToInclude.length > 0 ? countriesToInclude.join(',') : ''
        const dimensions = await callDimensionsAPIImmediate(
          disaggregations.RefArea,
          refAreas
        )
        if (dimensions) {
          validateAndUpdateFilters(dimensions)
          validateAndUpdateUnitMeasureAndFrequency(dimensions)
        }
      }

      dispatch(setSelectedAverages(allValues))
    } finally {
      setPendingAverageSelection(null)
    }
  }

  const handleDropdownCheckboxChange = async (event) => {
    const { value, checked } = event.target
    let currentSelectedValues = []
    if (checked) {
      currentSelectedValues = [...selectedAverages, value]
    } else {
      currentSelectedValues = selectedAverages.filter((v) => v !== value)
    }

    setPendingAverageSelection(currentSelectedValues)

    try {
      if (currentSelectedValues.length > 0) {
        let benchmarkList = []
        let averages = clone(currentSelectedValues)
        averages = averages.filter(
          (item) =>
            item !== aggregates.Region.id && item !== aggregates.IncomeGroup.id
        )

        if (chartType === chartTypes.Map && boundaryType === mapTypes.Region) {
          const regionCodes = grouping?.['HCL_WB_REGIONS']
            ? grouping['HCL_WB_REGIONS'].map((region) => region.id)
            : []
          averages = averages.filter((item) => regionCodes.includes(item))
        }

        if (currentSelectedValues.includes(aggregates.Region.id)) {
          const regionCountriesMapping = countryHierarchy?.REGION || []

          const regionList =
            chartType === chartTypes.Bar
              ? selectedCountriesForBar.length > 0 &&
                !selectedCountriesForBar.includes(
                  ALL_COUNTRIES_OBJECT.name.toLowerCase()
                )
                ? getMappingForBenchmark(
                    selectedCountriesForBar,
                    regionCountriesMapping
                  )
                : regionCountriesMapping?.filter((region) =>
                    disaggregationCountries?.find(
                      (country) => country.code === region.id
                    )
                  )
              : selectedCountries.length > 0
                ? getMappingForBenchmark(
                    selectedCountries,
                    regionCountriesMapping
                  )
                : regionCountriesMapping?.filter((region) =>
                    disaggregationCountries?.find(
                      (country) => country.code === region.id
                    )
                  )
          benchmarkList.push(...regionList.map((item) => item.id))
        }
        if (currentSelectedValues.includes(aggregates.IncomeGroup.id)) {
          const incomeGrpCountriesMapping = countryHierarchy?.INCOME || []

          const incomeGroupList =
            chartType === chartTypes.Bar
              ? selectedCountriesForBar.length > 0 &&
                !selectedCountriesForBar.includes(
                  ALL_COUNTRIES_OBJECT.name.toLowerCase()
                )
                ? getMappingForBenchmark(
                    selectedCountriesForBar,
                    incomeGrpCountriesMapping
                  )
                : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                    disaggregationCountries?.find(
                      (country) => country.code === incomeGroup.id
                    )
                  )
              : selectedCountries.length > 0
                ? getMappingForBenchmark(
                    selectedCountries,
                    incomeGrpCountriesMapping
                  )
                : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                    disaggregationCountries?.find(
                      (country) => country.code === incomeGroup.id
                    )
                  )
          benchmarkList.push(...incomeGroupList.map((item) => item.id))
        }
        //avoid duplicates in regional/income group aggregates and individual regions/income groups
        const refAreas = new Set([...benchmarkList, ...averages])
        // Also include selected countries in REF_AREA
        const countriesToInclude =
          chartType === chartTypes.Bar
            ? selectedCountriesForBar.filter(
                (item) => item !== ALL_COUNTRIES_OBJECT.name.toLowerCase()
              )
            : selectedCountries
        countriesToInclude.forEach((country) => refAreas.add(country))
        const allRefAreas = Array.from(refAreas).join(',')

        const dimensions = await callDimensionsAPIImmediate(
          disaggregations.RefArea,
          allRefAreas
        )

        if (dimensions) {
          validateAndUpdateFilters(dimensions)
          validateAndUpdateUnitMeasureAndFrequency(dimensions)
        }
      }

      dispatch(setSelectedAverages(currentSelectedValues))
    } finally {
      setPendingAverageSelection(null)
    }
  }

  useEffect(() => {
    if (!isSingleSelectionGrouping && selectedAverages.length > 0) {
      setQueryParam(QueryParams.Average, selectedAverages.join(','))
    } else if (!isSingleSelectionGrouping && selectedAverages.length === 0) {
      deleteQueryParams([QueryParams.Average])
    }
  }, [selectedAverages])

  const groupedOptions = {}
  multiselectOptions.forEach((item) => {
    const groupType = item.groupType
    if (!groupedOptions[groupType]) {
      groupedOptions[groupType] = []
    }
    groupedOptions[groupType].push(item)
  })

  const orderedGroups = [
    ...GROUP_ORDER.filter((key) => groupedOptions.hasOwnProperty(key)),
    ...Object.keys(groupedOptions).filter((key) => !GROUP_ORDER.includes(key)),
  ]

  const getGroupHeader = (groupType) => {
    if (
      groupType === 'AGGREGATE' ||
      groupType === WORLD_DEFAULT?.name?.toUpperCase()
    ) {
      return null
    }
    return `${groupType?.replace('_', ' ')} GROUPS`
  }

  return (
    <>
      {(isSingleSelectionGrouping && options.length > 1) ||
      (!isSingleSelectionGrouping && multiselectOptions.length > 0) ? (
        <div className="tui__label_text tui__secondary_dropdown tui__chart_dropdown tui_input_select">
          <label id={`${chartType}-average`}>Economy Groupings</label>
          {isSingleSelectionGrouping ? (
            <>
              <select
                htmlFor={`${chartType}-average`}
                value={selectedAverage[chartType]}
                onChange={async (event) => {
                  const averageValue = event.target.value

                  if (averageValue && averageValue !== NONE) {
                    const countriesToInclude =
                      chartType === chartTypes.Bar
                        ? (selectedCountriesForBar || []).filter(
                            (item) =>
                              item !== ALL_COUNTRIES_OBJECT.name.toLowerCase()
                          )
                        : selectedCountries || []
                    const refAreas = new Set([
                      averageValue,
                      ...countriesToInclude,
                    ])

                    const dimensions = await callDimensionsAPIImmediate(
                      disaggregations.RefArea,
                      Array.from(refAreas).join(',')
                    )

                    if (dimensions) {
                      validateAndUpdateFilters(dimensions)
                      validateAndUpdateUnitMeasureAndFrequency(dimensions)
                    }
                  } else {
                    const countriesToInclude =
                      chartType === chartTypes.Bar
                        ? (selectedCountriesForBar || []).filter(
                            (item) =>
                              item !== ALL_COUNTRIES_OBJECT.name.toLowerCase()
                          )
                        : selectedCountries || []
                    const refAreas =
                      countriesToInclude.length > 0
                        ? countriesToInclude.join(',')
                        : ''
                    const dimensions = await callDimensionsAPIImmediate(
                      disaggregations.RefArea,
                      refAreas
                    )
                    if (dimensions) {
                      validateAndUpdateFilters(dimensions)
                      validateAndUpdateUnitMeasureAndFrequency(dimensions)
                    }
                  }

                  dispatch(
                    setSelectedAverage({
                      chartType,
                      average: averageValue,
                    })
                  )
                  setQueryParam(QueryParams.Average, averageValue)
                  if (chartType === chartTypes.Pie) {
                    dispatch(resetSelectedCountries())
                    deleteQueryParams([QueryParams.CountrySelection])
                  }
                }}
              >
                {options}
              </select>
              <span className="select-arrow"></span>
            </>
          ) : (
            <div
              htmlFor={`${chartType}-average`}
              ref={averageDropdownRef}
              className="tui_selected_dropdown checkbox-dropdown"
            >
              <div
                className="tui_selected_value"
                onClick={() => setShowAverageDropdown(!showAverageDropdown)}
              >
                Select
              </div>
              {showAverageDropdown && (
                <div className="tui__checkbox_group">
                  <div
                    key={`${chartType}_selectAll_checkbox`}
                    className="tui__checkbox"
                    data-multi-checkbox={true}
                  >
                    <input
                      data-multi-checkbox={true}
                      className="tui__primary_checkbox"
                      type="checkbox"
                      id="selectAll"
                      value="Select All"
                      // name={selectedComparison} //TO-DO
                      checked={
                        (pendingAverageSelection !== null
                          ? isDimensionsLoading
                            ? pendingAverageSelection
                            : (selectedAverages ?? pendingAverageSelection)
                          : selectedAverages
                        ).length === multiselectOptions.length
                      }
                      disabled={hasDisabledOptions}
                      onChange={(event) => handleSelectAllChange(event)}
                    />
                    <label htmlFor="selectAll" data-multi-checkbox={true}>
                      <span data-multi-checkbox={true}>Select All</span>
                    </label>
                  </div>
                  {orderedGroups.map((groupType) => {
                    const items = groupedOptions[groupType]
                    if (!items || items.length === 0) return null

                    let sortedItems = [...items]
                    if (groupType === 'INCOME') {
                      sortedItems.sort((a, b) => {
                        const indexA = INCOME_GROUPS_ORDER.indexOf(
                          a.id.toUpperCase()
                        )
                        const indexB = INCOME_GROUPS_ORDER.indexOf(
                          b.id.toUpperCase()
                        )
                        return (
                          (indexA === -1 ? Infinity : indexA) -
                          (indexB === -1 ? Infinity : indexB)
                        )
                      })
                    } else if (
                      groupType !== WORLD_DEFAULT?.name?.toUpperCase() &&
                      groupType !== 'AGGREGATE'
                    ) {
                      sortedItems.sort((a, b) => a.value.localeCompare(b.value))
                    }

                    const groupHeader = getGroupHeader(groupType)

                    return (
                      <div key={`${chartType}_${groupType}_group`}>
                        {groupHeader && items.length > 0 && (
                          <div className="tui__dropdown_group_header">
                            {groupHeader}
                          </div>
                        )}
                        {sortedItems.map((item) => (
                          <div
                            key={`${chartType}_${item.id}_checkbox`}
                            className="tui__checkbox"
                            data-multi-checkbox={true}
                          >
                            <input
                              data-multi-checkbox={true}
                              className="tui__primary_checkbox"
                              type="checkbox"
                              id={`${chartType}_${item.id}`}
                              value={item.id}
                              checked={isChecked(item.id)}
                              disabled={isOptionDisabled(item.id)}
                              onChange={(event) =>
                                handleDropdownCheckboxChange(event)
                              }
                            />
                            <label
                              htmlFor={`${chartType}_${item.id}`}
                              data-multi-checkbox={true}
                            >
                              <span data-multi-checkbox={true}>
                                {item.value}
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </>
  )
}
export default AverageDropdown
